import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { FinancialService } from '@/services';
import { QueryParams } from '@/model';
import { CreateFinancialCreditCardPayload, UpdateFinancialPaymentMethodPayload } from '../request/financial';

export const useFinancialMonthlyPayment = (params?: QueryParams, enabled = true) => {
  const query = useQuery(
    ['GET_FINANCIAL_MONTHLY_PAYMENT', params],
    async () => {
      const [err, data] = await FinancialService.getFinancialMonthlyPayment(params);
      if (err) {
        throw err;
      }
      return data;
    },
    {
      refetchOnWindowFocus: false,
      enabled,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

export const useFinancialMonthlyPaymentById = (id: string, enabled = true) => {
  const query = useQuery(
    ['GET_FINANCIAL_MONTHLY_PAYMENT_BY_ID', id],
    async () => {
      const [err, data] = await FinancialService.getFinancialMonthlyPaymentById(id);
      if (err) {
        throw err;
      }
      return data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: enabled && !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

export const useFinancialMonthlyPaymentExportMutation = () => {
  return useMutation({
    mutationKey: ['FINANCIAL_MONTHLY_PAYMENT_EXPORT'],
    mutationFn: async (params?: QueryParams) => {
      const [err, data] = await FinancialService.exportFinancialMonthlyPayment(params);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

export const useFinancialMonthlyPaymentExportByIdMutation = () => {
  return useMutation({
    mutationKey: ['FINANCIAL_MONTHLY_PAYMENT_EXPORT_BY_ID'],
    mutationFn: async (id: string) => {
      const [err, data] = await FinancialService.exportFinancialMonthlyPaymentById(id);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

export const useFinancialOrdersBySubscriber = (id: string, params?: QueryParams, enabled?: boolean) => {
  const query = useQuery(
    ['GET_FINANCIAL_ORDERS_BY_SUBSCRIBER', id, params],
    async () => {
      const [err, data] = await FinancialService.getFinancialOrdersBySubscriber(id, params);
      if (err) {
        throw err;
      }
      return data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: enabled && !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

export const useFinancialCreditCardsBySubscriber = (id: string, enabled?: boolean) => {
  const query = useQuery(
    ['GET_FINANCIAL_CREDIT_CARDS_BY_SUBSCRIBER', id],
    async () => {
      const [err, data] = await FinancialService.getFinancialCreditCardsBySubscriber(id);
      if (err) {
        throw err;
      }
      return data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: enabled && !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

type UseFinancialOrdersExportBySubscriberMutationParams = {
  subscriber_id: string;
  params?: QueryParams;
};
export const useFinancialOrdersExportBySubscriberMutation = () => {
  return useMutation({
    mutationKey: ['FINANCIAL_ORDERS_EXPORT_BY_SUBSCRIBER'],
    mutationFn: async ({ subscriber_id, params }: UseFinancialOrdersExportBySubscriberMutationParams) => {
      const [err, data] = await FinancialService.exportFinancialOrdersBySubscriber(subscriber_id, params);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

type UseCreateFinancialCreditCardBySubscriberMutationParams = {
  subscriber_id: string;
  payload: CreateFinancialCreditCardPayload;
};
export const useCreateFinancialCreditCardBySubscriberMutation = () => {
  return useMutation({
    mutationKey: ['CREATE_FINANCIAL_CREDIT_CARD_BY_SUBSCRIBER'],
    mutationFn: async ({ subscriber_id, payload }: UseCreateFinancialCreditCardBySubscriberMutationParams) => {
      const [err, data] = await FinancialService.createFinancialCreditCardBySubscriber(subscriber_id, payload);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

type UseDeleteFinancialCreditCardBySubscriberMutationParams = {
  subscriber_id: string;
  card_id: string;
};
export const useDeleteFinancialCreditCardBySubscriberMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_FINANCIAL_CREDIT_CARD_BY_SUBSCRIBER'],
    mutationFn: async ({ subscriber_id, card_id }: UseDeleteFinancialCreditCardBySubscriberMutationParams) => {
      const [err, data] = await FinancialService.deleteFinancialCreditCardBySubscriber(subscriber_id, card_id);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

type UseUpdateFinancialPaymentMethodBySubscriberMutationParams = {
  subscriber_id: string;
  payload: UpdateFinancialPaymentMethodPayload;
};
export const useUpdateFinancialPaymentMethodBySubscriberMutation = () => {
  return useMutation({
    mutationKey: ['UPDATE_FINANCIAL_PAYMENT_METHOD_BY_SUBSCRIBER'],
    mutationFn: async ({ subscriber_id, payload }: UseUpdateFinancialPaymentMethodBySubscriberMutationParams) => {
      const [err, data] = await FinancialService.updateFinancialPaymentMethodBySubscriber(subscriber_id, payload);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};
